<template>
    <section v-if="bulk_message">
        <div class="d-flex align-center flex-wrap gap-3 my-2 py-2">
            <recipient-filter-control
                :value="selected_recipient_filter"
                :progress_detail="progress_detail"
                :message_state="message_state"
                @change="onFilterChange"
            />

            <v-spacer />

            <div class="d-flex align-center flex-wrap gap-4">
                <v-text-field
                    :value="full_text"
                    autocomplete="off"
                    label="Search"
                    prepend-inner-icon="mdi-magnify"
                    style="min-width: 300px; max-width: 320px;"
                    type="search"
                    dense
                    clearable
                    outlined
                    hide-details
                    @input="onSearchInputDebounced"
                />

                <v-menu
                    bottom
                    offset-y
                    slot="append-outer"
                >
                    <template #activator="{ on }">
                        <v-icon v-on="on">
                            mdi-dots-vertical
                        </v-icon>
                    </template>

                    <v-list dense>
                        <v-list-item @click="$emit('view-imports')">
                            <v-list-item-title>View excel files uploaded / imported</v-list-item-title>
                        </v-list-item>
                    </v-list>
                </v-menu>
            </div>
        </div>

        <v-data-table
            :headers="recipient_headers"
            :items="loading_recipients ? [] : recipients"
            :items-per-page="50"
            loading-text="Loading... Please wait"
            class="recipients-table elevation-0"
            style="border: 1px solid rgba(0, 0, 0, 0.12)"
            :loading="loading_recipients"
            :footer-props="{
                showFirstLastPage: true,
                itemsPerPage: 50,
                disableItemsPerPage: true,
                itemsPerPageOptions: [50, 100],
            }"
            :search="full_text"
            :options.sync="local_table_options"
            :server-items-length="num_recipients_under_current_filter"
        >
            <template #header.review_message>
                <span>Message</span>

                <v-tooltip
                    max-width="250"
                    bottom
                >
                    <template #activator="{ on, attrs }">
                        <v-icon
                            v-bind="attrs"
                            v-on="on"
                            small
                            color="primary"
                            class="ml-1"
                        >
                            mdi-information-outline
                        </v-icon>
                    </template>

                    <ul class="mb-0 pl-4">
                        <li>Attachments are not shown in this preview, but will be included in the actual message if present.</li>
                        <li>Links are displayed in full here, but will be shortened in the actual message if RedChirp link shortening is enabled.</li>
                    </ul>
                </v-tooltip>
            </template>

            <template #item.recipient_contact_first_name="{ item }">
                {{ prettyFirstName(item) }}
            </template>

            <template #item.recipient_contact_last_name="{ item }">
                {{ prettyLastName(item) }}
            </template>

            <template #item.recipient_endpoint="{ item }">
                {{ $rc.pretty(item.recipient_endpoint, 'endpoint_no_parens') }}
            </template>

            <template #item.locations="{ item }">
                {{ $rc.pretty(item.locations, 'comma_list') || '-' }}
            </template>

            <template #item.custom_message="{ item }">
                {{ prettyCustomMessage(item.custom_message) }}
            </template>

            <template
                v-if="$rc.$state.isAuthActorSystemAdmin()"
                #header.number_reassignment_check_since_date
            >
                <m-restricted-table-header-cell text="Number Reassignment Check Since Date" />
            </template>

            <template #item.number_reassignment_check_since_date="{ item }">
                {{ $rc.pretty(item.number_reassignment_check_since_date, 'date') }}
            </template>

            <template #item.review_message="{ item }">
                <div class="longform-cell">
                    <div class="message-preview-text" v-html="$rc.wrapHyperlinks(getMessagePreview(item))">
                    </div>
                </div>
            </template>

            <template #item.not_sent_message="{ item }">
                <div class="longform-cell">
                    {{ item.not_sent_message || '-' }}
                </div>
            </template>

            <template #item.state_detail="{ item }">
                {{$rc.enumValues('bulk_message_recipient_state_detail').find(o => o.value === item.state_detail)?.text
                    || '-'}}
            </template>

            <template #item.action="{ item }">
                <v-menu
                    v-if="getActionMenuItems(item).length > 0"
                    bottom
                    offset-y
                >
                    <template #activator="{ on, attrs }">
                        <v-icon
                            v-bind="attrs"
                            v-on="on"
                        >
                            mdi-dots-vertical-circle-outline
                        </v-icon>
                    </template>

                    <v-list
                        class="py-0"
                        dense
                    >
                        <v-list-item
                            v-for="action in getActionMenuItems(item)"
                            :key="action.key"
                            :disabled="action.disabled"
                            @click="action.handler(item)"
                        >
                            <v-list-item-title>{{ action.text }}</v-list-item-title>
                        </v-list-item>
                    </v-list>
                </v-menu>
            </template>

            <template #no-data>
                <div class="text-center pa-4">
                    No recipients found
                </div>
            </template>
        </v-data-table>

        <v-fade-transition>
            <div
                v-show="show_scroll_buttons"
                class="table-scroll-btn-container"
            >
                <v-tooltip top>
                    <template #activator="{ on, attrs }">
                        <v-btn
                            v-bind="attrs"
                            v-on="on"
                            color="primary"
                            class="table-scroll-btn"
                            fab
                            small
                            aria-label="Go to top of recipients table"
                            @click="scrollToTop"
                        >
                            <v-icon>mdi-chevron-double-up</v-icon>
                        </v-btn>
                    </template>
                    <span>Top of table</span>
                </v-tooltip>

                <v-tooltip top>
                    <template #activator="{ on, attrs }">
                        <v-btn
                            v-bind="attrs"
                            v-on="on"
                            color="primary"
                            class="table-scroll-btn"
                            fab
                            small
                            aria-label="Go to bottom of recipients table"
                            @click="scrollToBottom"
                        >
                            <v-icon>mdi-chevron-double-down</v-icon>
                        </v-btn>
                    </template>
                    <span>Bottom of table</span>
                </v-tooltip>
            </div>
        </v-fade-transition>
    </section>
</template>

<script>
import _ from 'lodash';
import RecipientFilterControl from '@/components/bulk/recipient-filter-control.vue';

export default {
    name: 'RecipientsTableReview',

    components: {
        RecipientFilterControl,
    },

    props: {
        bulk_message: {
            type: Object,
            required: true
        },
        message_state: {
            type: String,
            required: true
        },
        has_sent: {
            type: Boolean,
            required: true
        },
        selected_recipient_filter: {
            type: String,
            required: false,
            default: 'ready_to_send',
        },
        recipients: {
            type: Array,
            required: true,
        },
        num_recipients_under_current_filter: {
            type: Number,
            required: true,
        },
        recipient_extra_fields: {
            type: Array,
            required: false,
            default: () => [],
        },
        full_text: {
            type: String,
            required: false,
            default: '',
        },
        loading_recipients: {
            type: Boolean,
            required: false,
            default: false,
        },
        progress_detail: {
            type: Object,
            required: false
        },
        table_options: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            show_scroll_buttons: false,
            intersection_observer: null,
        };
    },

    computed: {
        local_table_options: {
            get() {
                return this.table_options;
            },
            set(value) {
                this.$emit('update:table_options', value);
            }
        },

        // TODO: edge case: first/last sorting doesn't work if custom_fields are used for it
        recipient_headers() {
            const ret = [
                { text: 'First', value: 'recipient_contact_first_name' },
                { text: 'Last', value: 'recipient_contact_last_name' },
                { text: 'Phone Number', value: 'recipient_endpoint', width: '140px' },
            ];

            switch (this.selected_recipient_filter) {
                case 'pending':
                    ret.push({ text: 'Custom Message', value: 'custom_message', sortable: false });
                    break;
                case 'filtered':
                    ret.push({ text: 'Filtered During', value: 'filter_context', sortable: true, width: '130px' });
                    ret.push({ text: 'Reason', value: 'state_detail', sortable: true });
                    ret.push({ text: 'Reason (Notes)', value: 'not_sent_message', sortable: true });
                    break;
                default:
                    ret.push({ text: 'Message', value: 'review_message', sortable: false }); // column label is set in slot
                    break;
            }

            ret.push({ text: '', value: 'action', sortable: false, width: '40px' });

            return ret;
        },
    },

    methods: {
        onFilterChange(value) {
            this.$emit('update-recipient-filter', value);
        },

        onSearchInputDebounced: _.debounce(function (value) {
            this.$emit('update-full-text', value);
        }, 751),

        prettyFirstName(item) {
            if (item.recipient_contact_first_name != null)
                return item.recipient_contact_first_name;

            if (item.extra_fields.first_name != null)
                return item.extra_fields.first_name;

            return null;
        },

        prettyLastName(item) {
            if (item.recipient_contact_last_name != null)
                return item.recipient_contact_last_name;

            if (item.extra_fields.last_name != null)
                return item.extra_fields.last_name;

            return null;
        },

        prettyCustomMessage(custom_message) {
            if (custom_message == null || custom_message == '') return 'No';
            return 'Yes';
        },

        getMessagePreview(item) {
            if (!this.bulk_message?.message_patterns?.length) {
                return '';
            }
            return item.review_message ?? '';
        },

        setupIntersectionObserver() {
            if (!this.$el) return;

            this.intersection_observer = new IntersectionObserver(
                (entries) => {
                    entries.forEach((entry) => {
                        this.updateButtonVisibility();
                    });
                },
                {
                    root: null,
                    threshold: 0,
                    rootMargin: '400px 0px 0px 0px'
                }
            );

            this.intersection_observer.observe(this.$el);

            window.addEventListener('scroll', this.handleScroll);
        },

        handleScroll() {
            this.updateButtonVisibility();
        },

        updateButtonVisibility() {
            const data_table = this.$el.querySelector('.v-data-table');
            if (!this.$el || !data_table) return;

            const table_rect = data_table.getBoundingClientRect();
            const table_height = table_rect.height;
            const table_top_above_viewport = table_rect.top < 0;
            const table_bottom_below_viewport = table_rect.bottom > window.innerHeight;

            this.show_scroll_buttons = table_height >= window.innerHeight && table_top_above_viewport && table_bottom_below_viewport;
        },

        scrollToTop() {
            if (this.$el) {
                window.scrollTo({
                    top: this.$el.offsetTop - 16,
                });
            }
        },

        scrollToBottom() {
            const data_table = this.$el?.querySelector('.v-data-table');
            if (data_table) {
                const table_bottom = data_table.getBoundingClientRect().bottom + window.scrollY;
                window.scrollTo({
                    top: table_bottom - window.innerHeight + 100,
                });
            }
        },

        getActionMenuItems(item) {
            const actions = [];

            if (this.selected_recipient_filter === 'ready_to_send') {
                actions.push({
                    key: 'send-test',
                    text: 'Send Test',
                    disabled: false,
                    handler: this.selectTestMessageRecipient,
                });
            }

            actions.push({
                key: 'delete-recipient',
                text: 'Delete Recipient',
                disabled: this.has_sent,
                handler: this.deleteRecipient,
            });

            return actions;
        },

        selectTestMessageRecipient(recipient) {
            this.$emit('select-test-message-recipient', recipient);
        },

        deleteRecipient(recipient) {
            this.$emit('delete-recipient', recipient);
        },
    },

    mounted() {
        this.$nextTick(() => {
            this.setupIntersectionObserver();
        });
    },

    beforeDestroy() {
        if (this.intersection_observer) {
            this.intersection_observer.disconnect();
        }
        window.removeEventListener('scroll', this.handleScroll);
    },
}
</script>

<style lang="scss" scoped>
.longform-cell {
    max-width: 700px;
}

.message-preview-text {
    font-family: 'Roboto Mono', monospace;
    padding: 0.6rem 0;
    position: relative;
    font-size: 13px;
    white-space: pre-line;
    overflow-wrap: break-word;
    line-height: 1.4;
    color: var(--v-secondary-darken3);
    transition: all 0.3s ease;
}

.table-scroll-btn-container {
    position: fixed;
    bottom: 2.5rem;
    right: 1.5rem;
    display: flex;
    flex-direction: row;
    gap: 0.5rem;
}

.table-scroll-btn {
    z-index: 10;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    cursor: pointer;

    &:hover {
        box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
    }
}

:deep(.filtered-recipient-row) {
    background: #fff5f5 !important;
}
</style>